import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { STAGE, gbp, isOpen } from "@/lib/stages";
import { ukDay } from "@/lib/time";
import { CaseStamp } from "@/components/portal/crm/CaseStamp";
import { inThisMonth } from "@/lib/caseflow";
import { TeamSeats } from "@/components/portal/TeamSeats";
import { PayoutNotes } from "@/components/portal/PayoutNotes";
import { AdminPreviewBanner } from "./Portal";
import {
  Btn, CellMain, CellSub, DeskSupport, Divider, Empty, Kv, LinkRow, Money, Note, PageBody, PageHead, Panel,
  RailCard, Sec, StatusChip, StatStrip, Tb, Td, Th,
} from "@/components/portal/crm/kit";

const num = { fontVariantNumeric: "tabular-nums" };
const QUIET_DAYS = 14;

/** The call-centre home — the floor first, cases second. An introducer who works cases themselves
    gets the ordinary workspace instead. */
export default function FloorHome() {
  const { leads, projection, me, isAdmin, pending } = useOutletContext();
  const nav = useNavigate();
  const [seats, setSeats] = useState(null);

  useEffect(() => {
    api.get("/refer/seats").then((r) => setSeats(r.data.items || [])).catch(() => setSeats([]));
  }, []);

  const unsigned = me?.agreement?.status !== "signed";
  const live = leads.filter((l) => isOpen(l.stage));
  const funded = leads.filter((l) => l.stage === "completed");
  const thisMonth = leads.filter((l) => inThisMonth(l.created_at));
  const viaAgents = leads.filter((l) => l.submitted_by_seat);
  const written = funded.reduce((s, l) => s + (l.amount_gbp || 0), 0);

  const list = seats || [];
  const activeSeats = list.filter((s) => s.status === "active" && s.activated);
  const awaiting = list.filter((s) => !s.activated && s.status !== "paused");
  const quiet = activeSeats.filter((s) => (s.quiet_days ?? 0) >= QUIET_DAYS);

  const t = projection?.totals;
  const projected = t ? (t.min_gbp === t.max_gbp ? gbp(t.max_gbp) : `${gbp(t.min_gbp)} – ${gbp(t.max_gbp)}`) : "—";

  const recent = useMemo(() => [...leads]
    .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
    .slice(0, 8), [leads]);

  const todo = useMemo(() => {
    const out = [];
    awaiting.forEach((s) => out.push({ key: `inv-${s.id}`, line: `${s.name} hasn't set a password yet`, go: () => nav("/introducer/account") }));
    quiet.forEach((s) => out.push({ key: `q-${s.id}`, line: `${s.name} — no case in ${s.quiet_days} days`, go: () => nav("/introducer/account") }));
    leads.forEach((l) => {
      if (l.docs_open > 0) out.push({ key: `d-${l.id}`, line: `${l.business_name || l.applicant_name} — ${l.docs_open} document${l.docs_open === 1 ? "" : "s"} outstanding`, go: () => nav(`/introducer/cases/${l.id}`) });
      else if (l.stage === "offer_made") out.push({ key: `o-${l.id}`, line: `${l.business_name || l.applicant_name} — offer to accept`, go: () => nav(`/introducer/cases/${l.id}`) });
    });
    return out.slice(0, 6);
  }, [awaiting, quiet, leads, nav]);

  return (
    <>
      {isAdmin && <AdminPreviewBanner what="call-centre portal" />}

      {unsigned && !pending && (
        <div className="warn" data-testid="agreement-callout" style={{ marginBottom: 22 }}>
          <span>
            <b>Your agreement is waiting on your signature.</b> It takes about twenty seconds in the portal, and
            commission is only payable on signed terms.{" "}
            <Link to="/introducer/paperwork" style={{ fontWeight: 800, textDecoration: "underline" }}>Read it and sign</Link>
          </span>
        </div>
      )}

      <PageHead
        eyebrow="Call centre"
        title="Your floor"
        lede="Your agents, what each of them is bringing in, and what the floor is worth this month. Every case they put in is yours — their share is whatever you set."
        testId="floor-head"
        actions={
          <>
            <Btn onClick={() => nav("/introducer/board")} testId="floor-board-cta">Put it on the wall</Btn>
            <Btn onClick={() => nav("/introducer/cases")} testId="floor-all-cases">All cases</Btn>
            <Btn kind="primary" onClick={() => nav("/introducer/submit")} testId="referrer-submit-cta">Submit a case</Btn>
          </>
        }
      />

      <PageBody
        rail={
          <>
            <DeskSupport body="Case management, lender chasing and payouts for the whole floor. Seven days a week." />

            <RailCard title="Needs you" testId="floor-needs-you">
              {todo.length === 0
                ? <div style={{ fontSize: 13, color: "var(--mut)" }}>Nothing waiting. Every live case is with a desk and every agent is active.</div>
                : todo.map((n) => <LinkRow key={n.key} label={n.line} onClick={n.go} testId={`floor-needs-${n.key}`} />)}
            </RailCard>

            <RailCard title="The floor" testId="floor-rail-stats">
              <Kv rows={[
                ["Agents on seats", String(activeSeats.length)],
                ["Awaiting set-up", String(awaiting.length)],
                ["Gone quiet", String(quiet.length), quiet.length ? { style: { color: "var(--amber)", fontWeight: 700 } } : undefined],
                ["Cases via agents", `${viaAgents.length} of ${leads.length}`],
              ]} />
              <Divider style={{ margin: "18px 0" }} />
              <LinkRow label="Earnings in full" onClick={() => nav("/introducer/performance")} testId="floor-rail-earnings" />
              <LinkRow label="Payout statements" onClick={() => nav("/introducer/statements")} testId="floor-rail-statements" />
              <LinkRow label="Your network" onClick={() => nav("/introducer/network")} testId="floor-rail-network" />
            </RailCard>

            <Note testId="floor-note">
              An agent only ever sees the cases they put in. Your commission, statements, network and paperwork stay
              with you as the account holder.
            </Note>
          </>
        }
      >
        <StatStrip
          testId="floor-stats"
          items={[
            { k: "Agents on seats", v: String(activeSeats.length), h: awaiting.length ? `${awaiting.length} still to set a password` : list.length ? "Everyone is set up" : "Add your first agent below", testId: "floor-stat-agents" },
            { k: "Cases this month", v: String(thisMonth.length), h: `${viaAgents.length} of ${leads.length} all time came from an agent`, testId: "floor-stat-month" },
            { k: "Live now", v: String(live.length), h: live.length ? `Worth ${projected} if they fund` : "Nothing live right now", testId: "floor-stat-live" },
            { k: "Funded", v: String(funded.length), h: funded.length ? `${gbp(written, { compact: true })} written` : "Not yet", tone: funded.length ? "green" : undefined, testId: "floor-stat-funded" },
          ]}
        />

        {pending && (
          <Sec>
            <Note testId="floor-pending-note">
              You can add your agents and send out their set-up links now. Cases open the moment the desk approves the
              account, so your floor can be ready to go on day one.
            </Note>
          </Sec>
        )}

        <Sec>
          <TeamSeats title="Your agents" />
        </Sec>

        <Sec>
          <PayoutNotes />
        </Sec>

        <Sec title="Latest from the floor" sub="The most recent cases across every agent — open one for the desks holding it, the documents and the thread."
             right={<Btn sm onClick={() => nav("/introducer/cases")} testId="floor-see-all">See all cases</Btn>}>
          <Panel pad={false} testId="floor-recent" style={{ overflow: "hidden" }}>
            {recent.length === 0 ? (
              <Empty testId="floor-recent-empty">Nothing in yet. The first case an agent submits shows here straight away.</Empty>
            ) : (
              <Tb testId="floor-recent-table" head={<>
                <Th>Client</Th><Th>Put in by</Th><Th>Finance</Th><Th className="r">Amount</Th><Th>Stage</Th><Th className="r">Received</Th>
              </>}>
                {recent.map((l) => {
                  const st = STAGE[l.stage] || STAGE.new;
                  return (
                    <tr key={l.id} data-testid={`floor-case-${l.id}`} onClick={() => nav(`/introducer/cases/${l.id}`)} style={{ cursor: "pointer" }}>
                      <Td className="lead">
                        <CellMain>{l.business_name || l.applicant_name || "—"}</CellMain>
                        <CellSub>{l.reference || "—"}</CellSub>
                        <div><CaseStamp at={l.created_at} testId={`floor-case-stamp-${l.id || l.reference}`} /></div>
                      </Td>
                      <Td>
                        <CellMain>{l.submitted_by_name || me?.name || "You"}</CellMain>
                        <CellSub>{l.submitted_by_seat ? "Agent seat" : "Account holder"}</CellSub>
                      </Td>
                      <Td><CellMain>{l.finance_type || "Business funding"}</CellMain></Td>
                      <Td className="r"><Money raw={gbp(l.amount_gbp)} tone="ink" /></Td>
                      <Td><StatusChip state={st} /></Td>
                      <Td className="r"><CellMain style={{ ...num, color: "var(--mut)", fontWeight: 500 }}>{ukDay(l.created_at)}</CellMain></Td>
                    </tr>
                  );
                })}
              </Tb>
            )}
          </Panel>
        </Sec>

        <Sec>
          <Note testId="floor-projection-note">{projection?.note || "Projected figures are a range, not a quote, and never a promise."}</Note>
        </Sec>
      </PageBody>
    </>
  );
}
